import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontFamilies, fontSizes, fontWeights, headings, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "@adobe/css-tools";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    backgroundColor = "backgroundColor",
    bannerHeading = "bannerHeading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    disableButton = "disableButton",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.backgroundColor]: string;
        [fields.bannerHeading]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary-white" | "secondary-white";
        [fields.disableButton]: boolean;
        [fields.customCSS]: string;
    };
}

export interface FullWidthImageCalloutStyles {
    contentWrapperStyles?: InjectableCss;
    heading?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
}

export const bannerStyles: FullWidthImageCalloutStyles = {
    contentWrapperStyles: {
        css: css`
            display: flex;
            width: 100%;
            padding: 64px 0px 64px 278px;
            flex-direction: column;
            align-items: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        
                        css`
                                padding: 32px 24px 64px 24px;                   
                        `,
                        css`
                                padding: 40px 24px 64px 24px;
                            `,
                        css`
                                padding: 40px 40px 64px 40px;
                            `,
                        css`
                                padding: 64px 40px 64px 40px;
                            `,
                        null,
                    ],
                    "minMax",
                )
            }
            `,
    },
    heading: {
        variant: "h2",
        css: css`
            color: ${themeColors.primary.white};
            line-height: ${headings.h2.lineHeight}; 
            letter-spacing: -0.68px;
            margin-bottom: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                font-size: ${headings.h2.fontSizeSmall};
                            `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    description: {
        css: css`
                color: ${themeColors.primary.white};
                line-height: ${lineHeights.medium};
                letter-spacing: -0.68px;
                margin-bottom: 32px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                margin-bottom: 24px;
                            `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
            `,
    },
    
};

export const Banner: FC<OwnProps> = ({ fields }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.image).url});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const wrapperStyles = {
        css: css`
            ${backgroundStyles}
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%
            height: 400px;
            color: ${themeColors.primary.white};
            overflow: hidden;
            flex-shrink: 0;

            width: 100vw;
            margin-left: calc(-50vw + 50%);
            left: 0;
            right: 0;

            background-position: center;
            background-repeat: no-repeat;
            background-size: cover;

            &::before {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 1;
            }

            > * {
            position: relative;
            z-index: 2;
            }
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
                <StyledWrapper className="banner-center-wrapper" {...bannerStyles.contentWrapperStyles}>
                    {fields.bannerHeading && (<Typography {...bannerStyles.heading} as="h2">{fields.bannerHeading}</Typography>)}
                    {fields.subheading && (
                        <Typography className="text-body-lg" {...bannerStyles.description}>
                            {parse(fields.subheading, parserOptions)}
                        </Typography>
                    )}
                    {!fields.disableButton && (
                        <Button className={`btn btn-${fields.variant}`} onClick={onClick}>
                            {fields.buttonLabel || title || url}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const banner: WidgetModule = {
    component: Banner,
    definition: {
        group: "Basic",
        displayName: "Full Width Image Callout (Banner)",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                tab: basicTab,

            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.bannerHeading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Description",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary White", value: "primary-white" },
                    { displayName: "Secondary White", value: "secondary-white" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary-white",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Translatable",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default banner;
